/**
 * Voice assistant in the widget bar (#1824). The state is readable at a glance
 * and never by colour alone: every state has its own word and picture.
 */

import { useLayoutEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, Mic, MicOff, Square } from 'lucide-react'
import { useVoiceAgent } from '@/contexts/VoiceAgentContext'
import type { WidgetProps } from '@/types/widget'
import { useVoiceCommands } from '../useVoiceCommands'
import { WIDGET_TILE_CLASS } from './tile'
import { voiceView, type VoiceView } from './voiceView'

const BAR_HEIGHTS = [12, 22, 34, 18, 40, 26, 14, 30, 44, 20, 36, 16]
const FOCUS_RING = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--theme-interactive-color)]'
const OUTLINE_PILL = `h-9 rounded-full border border-[var(--window-border-color)] px-3.5 text-[13px] text-[var(--text-color)] ${FOCUS_RING}`
const ROUND_BUTTON = `flex h-11 w-11 flex-none items-center justify-center rounded-full border border-[var(--window-border-color)] ${FOCUS_RING}`

const formatDuration = (seconds: number) =>
  `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`

function Visual({ view, bars, height }: { view: VoiceView; bars: number; height: number }) {
  const scale = height / 44
  if (view === 'processing') {
    return (
      <div className="flex flex-1 items-center gap-1" style={{ height }} aria-hidden>
        {[0, 200, 400].map((delay) => (
          <span key={delay} className="voice-dot" style={{ animationDelay: `${delay}ms` }} />
        ))}
      </div>
    )
  }
  if (view === 'muted') {
    return (
      <div className="flex min-w-0 flex-1 items-center" style={{ height }} aria-hidden>
        <span className="block h-0.5 flex-1 rounded-sm bg-[var(--window-border-color)]" />
      </div>
    )
  }
  return (
    <div
      className={`flex min-w-0 flex-1 items-center justify-between overflow-hidden ${
        view === 'speaking' ? 'text-[var(--theme-interactive-color)]' : 'text-[var(--text-color)]'
      }`}
      style={{ height }}
      aria-hidden
    >
      {Array.from({ length: bars }, (_, index) => (
        <span
          key={index}
          className="voice-bar"
          style={{ height: BAR_HEIGHTS[index % BAR_HEIGHTS.length] * scale, animationDelay: `${index * 90}ms` }}
        />
      ))}
    </div>
  )
}

export function VoiceBarWidget({ config, onOpenApp, staticPreview }: WidgetProps) {
  const { t } = useTranslation()
  const { state, status, connect, disconnect, toggleMute, sessionDuration } = useVoiceAgent()
  useVoiceCommands(onOpenApp, !staticPreview)

  const view = staticPreview ? 'idle' : voiceView(status, state.isMuted, state.error)
  const isSmall = config.size === 'small'
  const label = t('widgetBar.voice.name')
  const isMuted = view === 'muted'
  const accentStatus = view === 'processing' || view === 'speaking'
  const start = () => void connect()

  const text = view === 'muted' ? t('widgetBar.voice.mutedText') : state.currentLine
  const textRef = useRef<HTMLDivElement>(null)
  // Long answers stream past the two lines: keep the newest words in view.
  useLayoutEffect(() => {
    if (textRef.current) textRef.current.scrollTop = textRef.current.scrollHeight
  }, [text])

  const startButton = (
    <button
      type="button"
      aria-label={t('widgetBar.voice.start')}
      onClick={start}
      className={`flex h-[72px] w-[72px] flex-none items-center justify-center rounded-full bg-[var(--theme-interactive-color)] text-[var(--theme-interactive-foreground)] ${FOCUS_RING}`}
    >
      <Mic className="h-[30px] w-[30px]" aria-hidden />
    </button>
  )

  const controls = (
    <>
      <button
        type="button"
        aria-label={t(isMuted ? 'widgetBar.voice.unmute' : 'widgetBar.voice.mute')}
        aria-pressed={isMuted}
        onClick={toggleMute}
        className={`${ROUND_BUTTON} ${
          isMuted
            ? 'bg-[var(--text-color)] text-[var(--window-background)]'
            : 'text-[var(--text-color)]'
        }`}
      >
        {isMuted ? <MicOff className="h-5 w-5" aria-hidden /> : <Mic className="h-5 w-5" aria-hidden />}
      </button>
      <button
        type="button"
        aria-label={t('widgetBar.voice.end')}
        onClick={disconnect}
        className={`${ROUND_BUTTON} text-[var(--text-color)]`}
      >
        <Square className="h-[18px] w-[18px] fill-current" aria-hidden />
      </button>
    </>
  )

  if (view === 'errorMic' || view === 'errorNet') {
    const prefix = view === 'errorMic' ? 'errorMic' : 'errorNet'
    return (
      <section aria-label={label} role="alert" className={`${WIDGET_TILE_CLASS} flex-col items-start justify-center gap-1.5 ${isSmall ? 'p-3' : 'min-h-[172px] p-4'}`}>
        <span className={`flex items-center gap-2 font-bold ${isSmall ? 'text-[13px]' : 'text-[15px]'}`}>
          <AlertTriangle className="h-[18px] w-[18px] flex-none" aria-hidden />
          {t(`widgetBar.voice.${prefix}Title`)}
        </span>
        {!isSmall && <span className="text-[13px] leading-snug text-[var(--text-secondary)]">{t(`widgetBar.voice.${prefix}Text`)}</span>}
        <button type="button" onClick={start} className={`mt-1.5 ${OUTLINE_PILL}`}>
          {t('widgetBar.voice.retry')}
        </button>
      </section>
    )
  }

  if (view === 'connecting') {
    return (
      <section aria-label={label} aria-live="polite" className={`${WIDGET_TILE_CLASS} ${isSmall ? 'flex-col items-center justify-center gap-2 p-3' : 'items-center gap-6 p-4 pl-6'}`}>
        <span className="voice-ring" aria-hidden />
        <div className={`min-w-0 ${isSmall ? 'text-center' : ''}`}>
          {!isSmall && <div className="text-base font-bold">{t('widgetBar.voice.connecting')}</div>}
          <button type="button" onClick={disconnect} className={`${isSmall ? '' : 'mt-1.5'} ${OUTLINE_PILL}`}>
            {t('widgetBar.voice.cancel')}
          </button>
        </div>
      </section>
    )
  }

  if (view === 'idle') {
    if (isSmall) {
      return (
        <section aria-label={label} className={`${WIDGET_TILE_CLASS} flex-col justify-between p-3`}>
          <span className="text-xs font-medium text-[var(--text-secondary)]">{label}</span>
          <div className="mb-2.5 self-center">{startButton}</div>
        </section>
      )
    }
    return (
      <section aria-label={label} className={`${WIDGET_TILE_CLASS} min-h-[124px] items-center gap-4 p-4`}>
        {startButton}
        <div className="min-w-0">
          <div className="text-base font-bold">{label}</div>
          <div className="mt-1 text-[13px] leading-snug text-[var(--text-secondary)]">{t('widgetBar.voice.idleHint')}</div>
        </div>
      </section>
    )
  }

  const statusClass = `text-xs font-bold ${
    accentStatus ? 'text-[var(--theme-interactive-color)]' : isMuted ? 'text-[var(--text-secondary)]' : 'text-[var(--text-color)]'
  }`
  const timer = <span className="text-xs tabular-nums text-[var(--text-secondary)]">{formatDuration(sessionDuration)}</span>

  if (isSmall) {
    const shortStatus = { listening: 'listening', processing: 'processingShort', speaking: 'speaking', muted: 'mutedShort' }[view]
    return (
      <section aria-label={label} aria-live="polite" className={`${WIDGET_TILE_CLASS} flex-col justify-between p-3`}>
        <div className="flex items-center justify-between gap-1">
          <span className={statusClass}>{t(`widgetBar.voice.${shortStatus}`)}</span>
          {timer}
        </div>
        <Visual view={view} bars={14} height={32} />
        <div className="flex justify-center gap-3">{controls}</div>
      </section>
    )
  }

  return (
    <section aria-label={label} aria-live="polite" className={`${WIDGET_TILE_CLASS} min-h-[172px] flex-col justify-center gap-3 p-4`}>
      <div className="flex items-center justify-between">
        <span className={statusClass}>{t(`widgetBar.voice.${view}`)}</span>
        {timer}
      </div>
      <div className="flex h-11 items-center gap-2">
        <Visual view={view} bars={24} height={44} />
        {controls}
      </div>
      <div ref={textRef} className={`h-9 overflow-hidden text-[13px] leading-[18px] ${text ? 'text-[var(--text-color)]' : 'text-[var(--text-secondary)]'}`}>
        {text || (view === 'listening' ? t('widgetBar.voice.listeningHint') : null)}
      </div>
    </section>
  )
}
